import React from 'react';
import PdfViewer from './PdfViewer';
import ImageViewer from './ImageViewer';
import { isBrowserPreviewableImageByNameOrMime, isPdfByNameOrMime } from '@/utils/upload/uploadUtils.ts';

export interface FileViewerProps {
	fileUrl: string;
	title?: string;
	mode?: 'full' | 'compact';
	maxWidth?: number;
	mimeType?: string;
	filename?: string; // used for type detection when mimeType is missing
	// General viewer behavior props (work for both PDF and Image viewers)
	showExpandButton?: boolean;
	showExternalLink?: boolean;
	disableHoverEffects?: boolean;
	isHighlighted?: boolean;
	showPageControls?: boolean;
	/** Page to open a PDF preview at (1-based) — used to jump to a cited source's page. */
	initialPage?: number;
	showPreviewHeader?: boolean;
	fullscreenPageRequest?: { page: number; requestId: number } | null;
	unstyledCompact?: boolean;
	onPreviewAspectChange?: (aspect: number) => void;
}

/**
 * Type-driven router for file previews. Declaratively maps file types to concrete viewers.
 */
const FileViewer: React.FC<FileViewerProps> = ({
	fileUrl,
	title,
	mode = 'full',
	maxWidth,
	mimeType,
	filename,
	showExpandButton,
	showExternalLink,
	disableHoverEffects,
	isHighlighted,
	showPageControls,
	initialPage,
	showPreviewHeader,
	fullscreenPageRequest,
	unstyledCompact,
	onPreviewAspectChange,
}) => {
	if (isPdfByNameOrMime(filename, mimeType)) {
		return (
			<PdfViewer
				fileUrl={fileUrl}
				title={title}
				mode={mode}
				maxWidth={maxWidth}
				showExternalLink={showExternalLink}
				disableHoverEffects={disableHoverEffects}
				isHighlighted={isHighlighted}
				showExpandButton={showExpandButton}
				showPageControls={showPageControls}
				initialPage={initialPage}
				showPreviewHeader={showPreviewHeader}
				fullscreenPageRequest={fullscreenPageRequest}
				unstyledCompact={unstyledCompact}
				onPreviewAspectChange={onPreviewAspectChange}
			/>
		);
	}
	if (isBrowserPreviewableImageByNameOrMime(filename, mimeType)) {
		return (
			<ImageViewer
				fileUrl={fileUrl}
				title={title}
				mode={mode}
				maxWidth={maxWidth}
				showExternalLink={showExternalLink}
				disableHoverEffects={disableHoverEffects}
				isHighlighted={isHighlighted}
				showExpandButton={showExpandButton}
				showPreviewHeader={showPreviewHeader}
				unstyledCompact={unstyledCompact}
			/>
		);
	}
	return null;
};

export default FileViewer;
